/**
 * PoolAppMappingTree — nested org → project → app → version editor for
 * a pool's app mappings. Shows all eligible apps (tenancy-scoped), haystack
 * search, filter chips, and per-row toggles for Provision / Precache /
 * Pre-launch / Multiplex with tri-state inheritance indicators.
 *
 * Backend API:
 *   GET    /api/admin/pools/:id/eligible-tree
 *   PUT    /api/admin/pools/:id  { defaultProvision, defaultPrecache, defaultPrelaunch, defaultMultiplex }
 *   PATCH  /api/admin/pools/:id/apps/:appId  { precacheOverride, prelaunchOverride, multiplexOverride, provisionExcluded }
 *   PUT    /api/admin/pools/:id/apps/:appId/version-overrides  { versionScope, versionId?, ...overrides }
 *   DELETE /api/admin/pools/:id/apps/:appId/version-overrides?scope=...&versionId=...
 */

import { useCallback, useEffect, useMemo, useRef, useState, Fragment } from 'react';
import { useNavigate } from '@tanstack/react-router';
import styles from './PoolAppMappingTree.module.css';

// ---------------------------------------------------------------------------
// Types (mirror server response shape from handleAdminGetPoolEligibleTree)
// ---------------------------------------------------------------------------

type ToggleName = 'precache' | 'prelaunch' | 'multiplex';
// Provision is a special case: not an override flag on the app row, but driven
// by the dpa row's existence + provisionExcluded. Treated separately in the UI.
type PoolToggleDefaults = {
  provision: boolean;
  precache: boolean;
  prelaunch: boolean;
  multiplex: boolean;
};
type OverrideFlags = {
  precache: boolean | null;
  prelaunch: boolean | null;
  multiplex: boolean | null;
  provisionExcluded: boolean;
};
type AppOverrideFlags = OverrideFlags & { versionPin: string | null };

type VersionNode = {
  id: string;
  isLocal: boolean;
  gameEngine: string | null;
  operatingSystem: string | null;
  versionOverride: OverrideFlags | null;
  resolved: { provision: boolean; precache: boolean; prelaunch: boolean; multiplex: boolean };
};
type AppNode = {
  id: string;
  slug: string;
  name: string;
  defaultVersionId: string | null;
  appOverride: AppOverrideFlags | null;
  defaultScopeOverride: OverrideFlags | null;
  versions: VersionNode[];
  /** True iff this app has any pending (unsaved) edit. Set by the effective-tree
   *  pass that merges server state with pendingOps — undefined on the raw tree
   *  returned from the server. */
  _hasPending?: boolean;
};
type ProjectNode = { id: string; name: string; apps: AppNode[] };
type OrgNode = { id: string; name: string; projects: ProjectNode[] };

type EligibleTree = {
  poolDefaults: PoolToggleDefaults;
  tenancy: 'single' | 'organization' | 'multi';
  orgs: OrgNode[];
};

// ---------------------------------------------------------------------------
// Pending (staged) edit state
// ---------------------------------------------------------------------------

/** Pending edits to a single app's dpa row. Keys are the DB field names so the
 *  shape maps 1:1 onto the bulk-apply endpoint payload. Undefined = no pending
 *  change; null = pending-cleared (inherit from pool defaults). */
type PendingAppOverride = Partial<{
  precacheOverride: boolean | null;
  prelaunchOverride: boolean | null;
  multiplexOverride: boolean | null;
  provisionExcluded: boolean;
}>;

/** In-flight edit session. Persists locally until Save fires one bulk-apply
 *  call or Discard wipes it. Ordered by "most likely to diff from server":
 *  pool defaults at the top (one row), per-app overrides keyed by appId below.
 *  Version-level edits still go through the immediate per-toggle PATCH path
 *  in this first iteration — they're rarely batched, and staging them adds
 *  resolver-duplication we can do later. */
type PendingOps = {
  poolDefaults: Partial<PoolToggleDefaults>;
  appOverrides: Map<string, PendingAppOverride>;
};

const emptyPending = (): PendingOps => ({
  poolDefaults: {},
  appOverrides: new Map(),
});

function pendingCount(p: PendingOps): number {
  return Object.keys(p.poolDefaults).length + p.appOverrides.size;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface PoolAppMappingTreeProps {
  poolId: string;
  /** Pass through from parent — returns auth headers for fetch. */
  authHeaders: () => Promise<Record<string, string>>;
  /** Base URL for the API (e.g. https://api.dev.interlucent.ai). */
  apiBaseUrl: string;
  /** Called when an error occurs so the parent can surface it. */
  onError?: (message: string) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Compute the effective value of a single toggle for an app row, given the
 * current pool default and the app-level override row (if any).
 */
function appEffective(def: boolean, override: boolean | null | undefined): boolean {
  if (override === true) return true;
  if (override === false) return false;
  return def;
}

/**
 * Three-state rendering for a toggle button:
 *   - "on" + explicit: filled
 *   - "off" + explicit: outline
 *   - inherited: dashed border + "inh" label
 */
type ToggleDisplay = { on: boolean; inherited: boolean };

function displayFromOverride(poolDefault: boolean, override: boolean | null | undefined): ToggleDisplay {
  if (override === null || override === undefined) {
    return { on: poolDefault, inherited: true };
  }
  return { on: override, inherited: false };
}

/**
 * Aggregate provision state across a list of apps: "all" provisioned,
 * "none" provisioned, or "mixed".
 */
function aggregateProvision(apps: AppNode[], poolProvisionDefault: boolean): 'all' | 'none' | 'mixed' {
  if (apps.length === 0) return poolProvisionDefault ? 'all' : 'none';
  let hasProvisioned = false;
  let hasExcluded = false;
  for (const app of apps) {
    const excluded = app.appOverride?.provisionExcluded ?? false;
    const provisioned = !excluded && poolProvisionDefault;
    if (provisioned) hasProvisioned = true;
    else hasExcluded = true;
    if (hasProvisioned && hasExcluded) return 'mixed';
  }
  return hasProvisioned ? 'all' : 'none';
}

/**
 * Collect all apps under an org node (flattening projects).
 */
function allAppsInOrg(org: OrgNode): AppNode[] {
  const apps: AppNode[] = [];
  for (const p of org.projects) {
    for (const a of p.apps) apps.push(a);
  }
  return apps;
}

/**
 * True if this app-level row has any explicit override set. Used to paint a
 * left-edge accent stripe so the row reads as "overridden" at a glance.
 */
function hasAppOverride(app: AppNode): boolean {
  const o = app.appOverride;
  if (!o) return false;
  return (
    o.precache !== null || o.prelaunch !== null || o.multiplex !== null ||
    o.provisionExcluded || !!o.versionPin
  );
}

function hasDefaultScopeOverride(app: AppNode): boolean {
  const o = app.defaultScopeOverride;
  if (!o) return false;
  return (
    o.precache !== null || o.prelaunch !== null || o.multiplex !== null ||
    o.provisionExcluded
  );
}

function hasVersionOverride(v: VersionNode): boolean {
  const o = v.versionOverride;
  if (!o) return false;
  return (
    o.precache !== null || o.prelaunch !== null || o.multiplex !== null ||
    o.provisionExcluded
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PoolAppMappingTree({ poolId, authHeaders, apiBaseUrl, onError }: PoolAppMappingTreeProps) {
  const navigate = useNavigate();
  const [tree, setTree] = useState<EligibleTree | null>(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    provisioned: false,
    precached: false,
    prelaunched: false,
    multiplexed: false,
    overridesOnly: false,
  });
  const [expanded, setExpanded] = useState<{ orgs: Set<string>; projects: Set<string>; apps: Set<string> }>(() => ({
    orgs: new Set(),
    projects: new Set(),
    apps: new Set(),
  }));
  const [saving, setSaving] = useState(false);
  /** Staged (unsaved) edits accumulated while the operator tunes the tree.
   *  Pool-default and app-level toggles mutate this instead of firing PATCH
   *  on every click; the footer's "Save" flushes everything in one call. */
  const [pending, setPending] = useState<PendingOps>(emptyPending);
  const pendingTotal = pendingCount(pending);
  const hasPending = pendingTotal > 0;
  // Preview: track per-target pending state so we can show a spinner/disabled
  // while the admin preview-share endpoint is creating the share.
  const [previewPending, setPreviewPending] = useState<string | null>(null);
  const previewAbortRef = useRef<AbortController | null>(null);
  useEffect(() => () => previewAbortRef.current?.abort(), []);

  // Proactive cache toggle. When ON, every PoolDO config refresh fans
  // copy-batch out for newly-seen (appId, versionId) pairs. Useful for
  // testing — forces every mapped app into all 12 regional GCS caches
  // before any worker requests download URLs.
  const [cacheEnabled, setCacheEnabled] = useState<boolean | null>(null);
  const [cacheBusy, setCacheBusy] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const headers = await authHeaders();
        const res = await fetch(
          `${apiBaseUrl}/api/pool/${poolId}/proactive-cache`,
          { headers }
        );
        if (!res.ok) return;
        const data = (await res.json()) as { enabled?: boolean };
        if (!cancelled) setCacheEnabled(!!data.enabled);
      } catch {
        // Toggle is a testing convenience — silent failure is OK.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [poolId, authHeaders, apiBaseUrl]);

  const setProactiveCache = useCallback(
    async (enabled: boolean, hydrateNow = false) => {
      setCacheBusy(true);
      try {
        const headers = await authHeaders();
        const res = await fetch(
          `${apiBaseUrl}/api/pool/${poolId}/proactive-cache`,
          {
            method: 'POST',
            headers: { ...headers, 'content-type': 'application/json' },
            body: JSON.stringify({ enabled, hydrateNow }),
          }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as { enabled?: boolean };
        setCacheEnabled(!!data.enabled);
      } catch (err) {
        onError?.(err instanceof Error ? err.message : String(err));
      } finally {
        setCacheBusy(false);
      }
    },
    [poolId, authHeaders, apiBaseUrl, onError]
  );

  const fetchTree = useCallback(async () => {
    setLoading(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${apiBaseUrl}/api/admin/pools/${poolId}/eligible-tree`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as EligibleTree;
      setTree(data);
    } catch (err) {
      onError?.(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [poolId, authHeaders, apiBaseUrl, onError]);

  useEffect(() => {
    fetchTree();
  }, [fetchTree]);

  // -------------------------------------------------------------------------
  // Staged mutations — pool defaults and app-level toggles update `pending`
  // locally. The tree renders the prospective state immediately; the DB and
  // PoolDO are not touched until the footer's Save button flushes everything
  // in one bulk-apply call. Version-level edits still go through the
  // immediate per-toggle PUT path below (rarely batched, resolver would need
  // client-side duplication to stage — follow-up work).
  // -------------------------------------------------------------------------

  const stagePoolDefaults = useCallback((updates: Partial<PoolToggleDefaults>) => {
    setPending(prev => ({
      ...prev,
      poolDefaults: { ...prev.poolDefaults, ...updates },
    }));
  }, []);

  const stageAppOverride = useCallback((appId: string, updates: PendingAppOverride) => {
    setPending(prev => {
      const next = new Map(prev.appOverrides);
      const merged: PendingAppOverride = { ...next.get(appId), ...updates };
      // If all staged fields end up matching server "inherit" (all undefined),
      // drop the row. Callers pass explicit keys, so this mainly matters on
      // toggle-cycle-back-to-inherit: we keep the null marker so the bulk-apply
      // call actually clears the server override. Leave the row in place.
      next.set(appId, merged);
      return { ...prev, appOverrides: next };
    });
  }, []);

  const saveAll = useCallback(async () => {
    if (!hasPending) return;
    setSaving(true);
    try {
      const headers = await authHeaders();
      const body: Record<string, unknown> = {};
      if (Object.keys(pending.poolDefaults).length > 0) body.poolDefaults = pending.poolDefaults;
      if (pending.appOverrides.size > 0) {
        body.appOverrides = Array.from(pending.appOverrides.entries()).map(([appId, fields]) => ({ appId, ...fields }));
      }
      const res = await fetch(`${apiBaseUrl}/api/admin/pools/${poolId}/bulk-apply`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setPending(emptyPending());
      await fetchTree();
    } catch (err) {
      onError?.(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }, [pending, hasPending, poolId, authHeaders, apiBaseUrl, onError, fetchTree]);

  const discardAll = useCallback(() => {
    if (!hasPending) return;
    if (!window.confirm(`Discard ${pendingTotal} pending change${pendingTotal === 1 ? '' : 's'}?`)) return;
    setPending(emptyPending());
  }, [hasPending, pendingTotal]);

  const putVersionOverride = useCallback(async (
    appId: string,
    scope: 'explicit' | 'default',
    versionId: string | null,
    overrides: Partial<{ precacheOverride: boolean | null; prelaunchOverride: boolean | null; multiplexOverride: boolean | null; provisionExcluded: boolean }>,
  ) => {
    setSaving(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${apiBaseUrl}/api/admin/pools/${poolId}/apps/${appId}/version-overrides`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          versionScope: scope,
          versionId,
          precacheOverride: overrides.precacheOverride ?? null,
          prelaunchOverride: overrides.prelaunchOverride ?? null,
          multiplexOverride: overrides.multiplexOverride ?? null,
          provisionExcluded: overrides.provisionExcluded ?? false,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await fetchTree();
    } catch (err) {
      onError?.(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }, [poolId, authHeaders, apiBaseUrl, onError, fetchTree]);

  // -------------------------------------------------------------------------
  // Preview: create a pool-scoped share via admin endpoint and navigate to
  // the player route. Admin shares carry enough context to launch without
  // the user needing project-scope permissions.
  // -------------------------------------------------------------------------

  const launchPreview = useCallback(async (appId: string, versionId?: string) => {
    const key = versionId ? `${appId}:${versionId}` : appId;
    if (previewPending) return;
    setPreviewPending(key);
    previewAbortRef.current?.abort();
    const controller = new AbortController();
    previewAbortRef.current = controller;
    try {
      const headers = await authHeaders();
      const res = await fetch(`${apiBaseUrl}/api/admin/pools/${poolId}/preview-share`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ appId, versionId }),
        signal: controller.signal,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const payload = (await res.json()) as { id?: string; error?: string };
      if (!payload.id) throw new Error(payload.error ?? 'No share id returned');
      navigate({ to: '/preview/$', params: { _splat: payload.id } });
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      onError?.(err instanceof Error ? err.message : String(err));
    } finally {
      setPreviewPending((p) => (p === key ? null : p));
    }
  }, [apiBaseUrl, authHeaders, navigate, onError, poolId, previewPending]);

  // -------------------------------------------------------------------------
  // Reset: clear every override on this pool (app-level, default-scope, and
  // per-version). Pool-level defaults stay put. Gated by a confirm because
  // this nukes configuration the user may have spent time dialing in.
  // -------------------------------------------------------------------------

  const resetOverrides = useCallback(async () => {
    if (hasPending) {
      onError?.('Save or discard pending changes before resetting overrides.');
      return;
    }
    if (!window.confirm(
      'Reset every app, version, and default-scope override on this pool back to inherit-from-defaults?\n\nThis does not change pool defaults or remove app mappings.'
    )) {
      return;
    }
    setSaving(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${apiBaseUrl}/api/admin/pools/${poolId}/reset-mapping-overrides`, {
        method: 'POST',
        headers,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await fetchTree();
    } catch (err) {
      onError?.(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }, [apiBaseUrl, authHeaders, fetchTree, onError, poolId, hasPending]);

  // -------------------------------------------------------------------------
  // Bulk provision: set provisionExcluded for every app in an org or project.
  // -------------------------------------------------------------------------

  const bulkProvision = useCallback(async (scope: 'org' | 'project', scopeId: string, provisionExcluded: boolean) => {
    if (hasPending) {
      onError?.('Save or discard pending changes before applying a bulk action.');
      return;
    }
    setSaving(true);
    try {
      const headers = await authHeaders();
      const res = await fetch(`${apiBaseUrl}/api/admin/pools/${poolId}/bulk-provision`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ scope, scopeId, provisionExcluded }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await fetchTree();
    } catch (err) {
      onError?.(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }, [poolId, authHeaders, apiBaseUrl, onError, fetchTree, hasPending]);

  // -------------------------------------------------------------------------
  // Cycle handlers — inherit → on → off → inherit for each level's toggle.
  // App-level cycles stage into `pending`. Version-level cycles stay on the
  // immediate per-toggle path (see note above stagePoolDefaults).
  // -------------------------------------------------------------------------

  const cycleAppToggle = useCallback((appId: string, name: ToggleName, current: boolean | null | undefined) => {
    // `current` is already the effective value (server-merged-with-pending) —
    // see effectiveTree below. Cycling operates on what the operator sees.
    const next = current === null || current === undefined ? true : current === true ? false : null;
    const field = name === 'precache' ? 'precacheOverride' : name === 'prelaunch' ? 'prelaunchOverride' : 'multiplexOverride';
    stageAppOverride(appId, { [field]: next });
  }, [stageAppOverride]);

  const cycleVersionToggle = useCallback((
    app: AppNode,
    version: VersionNode,
    name: ToggleName,
  ) => {
    const existing = version.versionOverride;
    const current = existing ? existing[name] : null;
    const next = current === null || current === undefined ? true : current === true ? false : null;
    // Build the full override payload — we need to preserve other fields or explicitly null them.
    const nextOverride = {
      precacheOverride: name === 'precache' ? next : (existing?.precache ?? null),
      prelaunchOverride: name === 'prelaunch' ? next : (existing?.prelaunch ?? null),
      multiplexOverride: name === 'multiplex' ? next : (existing?.multiplex ?? null),
      provisionExcluded: existing?.provisionExcluded ?? false,
    };
    putVersionOverride(app.id, 'explicit', version.id, nextOverride);
  }, [putVersionOverride]);

  const cycleDefaultScopeToggle = useCallback((app: AppNode, name: ToggleName) => {
    const existing = app.defaultScopeOverride;
    const current = existing ? existing[name] : null;
    const next = current === null || current === undefined ? true : current === true ? false : null;
    const nextOverride = {
      precacheOverride: name === 'precache' ? next : (existing?.precache ?? null),
      prelaunchOverride: name === 'prelaunch' ? next : (existing?.prelaunch ?? null),
      multiplexOverride: name === 'multiplex' ? next : (existing?.multiplex ?? null),
      provisionExcluded: existing?.provisionExcluded ?? false,
    };
    putVersionOverride(app.id, 'default', null, nextOverride);
  }, [putVersionOverride]);

  // -------------------------------------------------------------------------
  // Filtering
  // -------------------------------------------------------------------------

  const filteredTree = useMemo<EligibleTree | null>(() => {
    if (!tree) return null;
    const q = search.trim().toLowerCase();
    const matchesSearch = (haystack: string) => !q || haystack.toLowerCase().includes(q);

    const versionPasses = (v: VersionNode): boolean => {
      if (filters.provisioned && !v.resolved.provision) return false;
      if (filters.precached && !v.resolved.precache) return false;
      if (filters.prelaunched && !v.resolved.prelaunch) return false;
      if (filters.multiplexed && !v.resolved.multiplex) return false;
      if (filters.overridesOnly && !v.versionOverride) return false;
      return true;
    };

    const orgs: OrgNode[] = [];
    for (const org of tree.orgs) {
      const projects: ProjectNode[] = [];
      for (const project of org.projects) {
        const apps: AppNode[] = [];
        for (const app of project.apps) {
          const appTextMatches = matchesSearch(`${org.name} ${project.name} ${app.name} ${app.slug}`);
          const filteredVersions: VersionNode[] = [];
          for (const v of app.versions) {
            if (appTextMatches || matchesSearch(v.id)) {
              if (versionPasses(v)) filteredVersions.push(v);
            }
          }
          if (filteredVersions.length > 0 || (appTextMatches && app.versions.length === 0)) {
            apps.push({ ...app, versions: filteredVersions });
          }
        }
        if (apps.length > 0) projects.push({ ...project, apps });
      }
      if (projects.length > 0) orgs.push({ ...org, projects });
    }
    return { ...tree, orgs };
  }, [tree, search, filters]);

  // Effective tree = server-fetched tree with pending staged edits merged in.
  // All render paths below read from this, so toggling a staged edit updates
  // the UI immediately while Save is still pending. Preserves `filteredTree`
  // in the dependency chain so search/filter behavior is unchanged.
  const effectiveTree = useMemo<EligibleTree | null>(() => {
    if (!filteredTree) return null;
    const hasPoolDefaultsPending = Object.keys(pending.poolDefaults).length > 0;
    if (!hasPoolDefaultsPending && pending.appOverrides.size === 0) return filteredTree;

    const orgs = filteredTree.orgs.map(org => ({
      ...org,
      projects: org.projects.map(project => ({
        ...project,
        apps: project.apps.map(app => {
          const p = pending.appOverrides.get(app.id);
          if (!p) return app;
          // Merge pending fields on top of app.appOverride. Reading a key out
          // of PendingAppOverride returns undefined if not staged, so we fall
          // back to the server value for that field.
          return {
            ...app,
            _hasPending: true,
            appOverride: {
              versionPin: app.appOverride?.versionPin ?? null,
              precache: 'precacheOverride' in p ? p.precacheOverride! : (app.appOverride?.precache ?? null),
              prelaunch: 'prelaunchOverride' in p ? p.prelaunchOverride! : (app.appOverride?.prelaunch ?? null),
              multiplex: 'multiplexOverride' in p ? p.multiplexOverride! : (app.appOverride?.multiplex ?? null),
              provisionExcluded: 'provisionExcluded' in p ? p.provisionExcluded! : (app.appOverride?.provisionExcluded ?? false),
            },
          };
        }),
      })),
    }));
    return {
      ...filteredTree,
      poolDefaults: { ...filteredTree.poolDefaults, ...pending.poolDefaults },
      orgs,
    };
  }, [filteredTree, pending]);

  // When a search is active, expand everything so matches are visible.
  useEffect(() => {
    if (!search.trim() || !effectiveTree) return;
    const orgs = new Set<string>();
    const projects = new Set<string>();
    const apps = new Set<string>();
    for (const org of effectiveTree.orgs) {
      orgs.add(org.id);
      for (const project of org.projects) {
        projects.add(project.id);
        for (const app of project.apps) apps.add(app.id);
      }
    }
    setExpanded({ orgs, projects, apps });
  }, [search, effectiveTree]);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  if (loading && !tree) return <div className={styles.message}>Loading…</div>;
  if (!effectiveTree) return <div className={styles.message}>No tree loaded.</div>;

  const defaults = effectiveTree.poolDefaults;

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <input
          type="search"
          className={styles.search}
          placeholder="Search orgs, projects, apps, versions…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className={styles.filters}>
          <FilterChip label="Provisioned" active={filters.provisioned} onToggle={() => setFilters(f => ({ ...f, provisioned: !f.provisioned }))} />
          <FilterChip label="Precached" active={filters.precached} onToggle={() => setFilters(f => ({ ...f, precached: !f.precached }))} />
          <FilterChip label="Pre-launched" active={filters.prelaunched} onToggle={() => setFilters(f => ({ ...f, prelaunched: !f.prelaunched }))} />
          <FilterChip label="Multiplexed" active={filters.multiplexed} onToggle={() => setFilters(f => ({ ...f, multiplexed: !f.multiplexed }))} />
          <FilterChip label="Overrides only" active={filters.overridesOnly} onToggle={() => setFilters(f => ({ ...f, overridesOnly: !f.overridesOnly }))} />
        </div>
        <button
          className={styles.resetButton}
          onClick={resetOverrides}
          disabled={saving || hasPending}
          title={hasPending
            ? 'Save or discard pending changes first.'
            : 'Clear every app, version, and default-scope override. Pool defaults stay put.'}
        >
          Reset overrides
        </button>
        <button
          className={styles.resetButton}
          onClick={() => setProactiveCache(!cacheEnabled)}
          disabled={cacheBusy || cacheEnabled === null}
          title="When ON, every config refresh fires R2 → regional GCS copy for any newly-seen app+version on this pool."
        >
          Cache: {cacheEnabled === null ? '…' : cacheEnabled ? 'ON' : 'OFF'}
        </button>
        <button
          className={styles.resetButton}
          onClick={() => setProactiveCache(true, true)}
          disabled={cacheBusy}
          title="Re-fire R2 → GCS copy-batch for every app currently mapped to this pool. Idempotent — already-cached objects are skipped."
        >
          Hydrate now
        </button>
        <div className={styles.tenancyBadge}>tenancy: {effectiveTree.tenancy}</div>
      </div>

      <div className={styles.tree}>
        <div className={styles.header}>
          <div className={styles.headerName}>Name</div>
          <div className={styles.headerCol}>Provision</div>
          <div className={styles.headerCol}>Precache</div>
          <div className={styles.headerCol}>Pre-launch</div>
          <div className={styles.headerCol}>Multiplex</div>
          <div className={styles.headerCol}>Play</div>
        </div>

        {/* Pool-wide defaults live as a row inside the tree so the columns
            line up and the "what do inherited cells resolve to" question
            has one obvious visual answer. */}
        <DefaultsRow defaults={defaults} onChange={stagePoolDefaults} saving={saving} />

        {effectiveTree.orgs.map(org => (
          <OrgRow
            key={org.id}
            org={org}
            defaults={defaults}
            expanded={expanded}
            setExpanded={setExpanded}
            onAppToggle={cycleAppToggle}
            onDefaultScopeToggle={cycleDefaultScopeToggle}
            onVersionToggle={cycleVersionToggle}
            onAppProvisionExcluded={(appId, excluded) => stageAppOverride(appId, { provisionExcluded: excluded })}
            onLaunchPreview={launchPreview}
            previewPending={previewPending}
            onBulkProvision={bulkProvision}
            saving={saving}
          />
        ))}
      </div>

      {hasPending && (
        <div className={styles.stagedFooter}>
          <span className={styles.stagedFooterCount}>
            {pendingTotal} pending change{pendingTotal === 1 ? '' : 's'} · not yet saved
          </span>
          <div className={styles.stagedFooterActions}>
            <button
              className={styles.stagedFooterDiscard}
              onClick={discardAll}
              disabled={saving}
            >
              Discard
            </button>
            <button
              className={styles.stagedFooterSave}
              onClick={saveAll}
              disabled={saving}
              title="Apply all staged edits and push a single config update to the PoolDO."
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * Row that anchors the top of the tree showing the pool-wide defaults. Every
 * inherited (dashed) cell below this row resolves through these values, so
 * colocating them with the tree columns removes guesswork.
 */
function DefaultsRow({
  defaults,
  onChange,
  saving,
}: {
  defaults: PoolToggleDefaults;
  onChange: (updates: Partial<PoolToggleDefaults>) => void;
  saving: boolean;
}) {
  return (
    <div className={`${styles.row} ${styles.defaultsRow}`}>
      <div className={styles.defaultsRowLabel}>Pool defaults</div>
      <div className={styles.cells}>
        <CellToggle on={defaults.provision} inherited={false} disabled={saving} onClick={() => onChange({ provision: !defaults.provision })} />
        <CellToggle on={defaults.precache} inherited={false} disabled={saving} onClick={() => onChange({ precache: !defaults.precache })} />
        <CellToggle
          on={defaults.prelaunch}
          inherited={false}
          disabled={saving || !defaults.precache}
          disabledReason={!defaults.precache ? 'Pre-launch requires Precache on' : undefined}
          onClick={() => onChange({ prelaunch: !defaults.prelaunch })}
        />
        <CellToggle on={defaults.multiplex} inherited={false} disabled={saving} onClick={() => onChange({ multiplex: !defaults.multiplex })} />
      </div>
      <CellSpacer />
    </div>
  );
}

function FilterChip({ label, active, onToggle }: { label: string; active: boolean; onToggle: () => void }) {
  return (
    <button className={`${styles.chip} ${active ? styles.chipActive : ''}`} onClick={onToggle}>
      {label}
    </button>
  );
}

interface RowCallbacks {
  onAppToggle: (appId: string, name: ToggleName, current: boolean | null | undefined) => void;
  onDefaultScopeToggle: (app: AppNode, name: ToggleName) => void;
  onVersionToggle: (app: AppNode, version: VersionNode, name: ToggleName) => void;
  onAppProvisionExcluded: (appId: string, excluded: boolean) => void;
  onLaunchPreview: (appId: string, versionId?: string) => void;
  previewPending: string | null;
  onBulkProvision: (scope: 'org' | 'project', scopeId: string, provisionExcluded: boolean) => void;
  saving: boolean;
}

function OrgRow({
  org, defaults, expanded, setExpanded, ...cb
}: {
  org: OrgNode;
  defaults: PoolToggleDefaults;
  expanded: { orgs: Set<string>; projects: Set<string>; apps: Set<string> };
  setExpanded: React.Dispatch<React.SetStateAction<{ orgs: Set<string>; projects: Set<string>; apps: Set<string> }>>;
} & RowCallbacks) {
  const isOpen = expanded.orgs.has(org.id);
  const toggle = () => setExpanded(prev => {
    const next = { ...prev, orgs: new Set(prev.orgs) };
    if (next.orgs.has(org.id)) next.orgs.delete(org.id); else next.orgs.add(org.id);
    return next;
  });
  const apps = allAppsInOrg(org);
  const provState = aggregateProvision(apps, defaults.provision);
  // Click cycles: all → none, mixed/none → all
  const handleProvisionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    cb.onBulkProvision('org', org.id, provState === 'all');
  };
  return (
    <>
      <div className={`${styles.row} ${styles.orgRow}`} onClick={toggle}>
        <div className={styles.name}>
          <span className={styles.chevron}>{isOpen ? '▾' : '▸'}</span>
          <span className={styles.orgName}>{org.name}</span>
          <span className={styles.subcount}>{org.projects.length} project{org.projects.length !== 1 ? 's' : ''}</span>
        </div>
        <div className={styles.cells}>
          <BulkProvisionCell
            state={provState}
            disabled={cb.saving}
            onClick={handleProvisionClick}
            title={provState === 'all'
              ? 'Exclude all apps in this org from provisioning'
              : provState === 'none'
              ? 'Provision all apps in this org'
              : 'Mixed — click to provision all apps in this org'}
          />
        </div>
        <CellSpacer />
      </div>
      {isOpen && org.projects.map(project => (
        <ProjectRow
          key={project.id}
          project={project}
          defaults={defaults}
          expanded={expanded}
          setExpanded={setExpanded}
          {...cb}
        />
      ))}
    </>
  );
}

function ProjectRow({
  project, defaults, expanded, setExpanded, ...cb
}: {
  project: ProjectNode;
  defaults: PoolToggleDefaults;
  expanded: { orgs: Set<string>; projects: Set<string>; apps: Set<string> };
  setExpanded: React.Dispatch<React.SetStateAction<{ orgs: Set<string>; projects: Set<string>; apps: Set<string> }>>;
} & RowCallbacks) {
  const isOpen = expanded.projects.has(project.id);
  const toggle = () => setExpanded(prev => {
    const next = { ...prev, projects: new Set(prev.projects) };
    if (next.projects.has(project.id)) next.projects.delete(project.id); else next.projects.add(project.id);
    return next;
  });
  const provState = aggregateProvision(project.apps, defaults.provision);
  const handleProvisionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    cb.onBulkProvision('project', project.id, provState === 'all');
  };
  return (
    <>
      <div className={`${styles.row} ${styles.projectRow}`} onClick={toggle}>
        <div className={styles.name}>
          <span className={styles.chevron}>{isOpen ? '▾' : '▸'}</span>
          <span className={styles.projectName}>{project.name}</span>
          <span className={styles.subcount}>{project.apps.length} app{project.apps.length !== 1 ? 's' : ''}</span>
        </div>
        <div className={styles.cells}>
          <BulkProvisionCell
            state={provState}
            disabled={cb.saving}
            onClick={handleProvisionClick}
            title={provState === 'all'
              ? 'Exclude all apps in this project from provisioning'
              : provState === 'none'
              ? 'Provision all apps in this project'
              : 'Mixed — click to provision all apps in this project'}
          />
        </div>
        <CellSpacer />
      </div>
      {isOpen && project.apps.map(app => (
        <AppRow
          key={app.id}
          app={app}
          defaults={defaults}
          expanded={expanded}
          setExpanded={setExpanded}
          {...cb}
        />
      ))}
    </>
  );
}

function AppRow({
  app, defaults, expanded, setExpanded,
  onAppToggle, onDefaultScopeToggle, onVersionToggle, onAppProvisionExcluded,
  onLaunchPreview, previewPending,
}: {
  app: AppNode;
  defaults: PoolToggleDefaults;
  expanded: { orgs: Set<string>; projects: Set<string>; apps: Set<string> };
  setExpanded: React.Dispatch<React.SetStateAction<{ orgs: Set<string>; projects: Set<string>; apps: Set<string> }>>;
} & RowCallbacks) {
  const isOpen = expanded.apps.has(app.id);
  const toggle = () => setExpanded(prev => {
    const next = { ...prev, apps: new Set(prev.apps) };
    if (next.apps.has(app.id)) next.apps.delete(app.id); else next.apps.add(app.id);
    return next;
  });

  const appOverride = app.appOverride;
  const provisionExcluded = appOverride?.provisionExcluded ?? false;
  // Effective app-level toggle states (before version overrides stack)
  const pre = displayFromOverride(defaults.precache, appOverride?.precache);
  const pl = displayFromOverride(defaults.prelaunch, appOverride?.prelaunch);
  const mx = displayFromOverride(defaults.multiplex, appOverride?.multiplex);
  const overridden = hasAppOverride(app);
  // rowPending takes visual priority over rowOverridden — an operator mid-edit
  // cares more about what's about to save than what's already saved.
  const pendingClass = app._hasPending ? styles.rowPending : (overridden ? styles.rowOverridden : '');
  const rowClass = `${styles.row} ${styles.appRow} ${pendingClass}`;

  return (
    <Fragment>
      <div className={rowClass}>
        <div className={styles.name} onClick={toggle}>
          <span className={styles.chevron}>{isOpen ? '▾' : '▸'}</span>
          <span className={styles.appName}>{app.name}</span>
          <span className={styles.appSlug}>{app.slug}</span>
          {app.defaultVersionId && <span className={styles.defaultBadge}>default: {app.defaultVersionId}</span>}
        </div>
        <div className={styles.cells}>
          <CellToggle
            on={!provisionExcluded && defaults.provision}
            inherited={!appOverride || !provisionExcluded}
            onClick={() => onAppProvisionExcluded(app.id, !provisionExcluded)}
            title={provisionExcluded ? 'Click to include this app in provisioning' : 'Click to exclude this app from provisioning'}
          />
          <CellToggle
            on={pre.on}
            inherited={pre.inherited}
            onClick={() => onAppToggle(app.id, 'precache', appOverride?.precache)}
          />
          <CellToggle
            on={pl.on}
            inherited={pl.inherited}
            disabled={!pre.on}
            disabledReason={!pre.on ? 'Pre-launch requires Precache' : undefined}
            onClick={() => onAppToggle(app.id, 'prelaunch', appOverride?.prelaunch)}
          />
          <CellToggle
            on={mx.on}
            inherited={mx.inherited}
            onClick={() => onAppToggle(app.id, 'multiplex', appOverride?.multiplex)}
          />
        </div>
        <PreviewCell
          targetKey={app.id}
          disabled={!app.defaultVersionId}
          pending={previewPending === app.id}
          onClick={() => onLaunchPreview(app.id)}
          title={app.defaultVersionId
            ? `Launch a preview session using the app's default version (${app.defaultVersionId})`
            : 'No default version set — open a version to preview'}
        />
      </div>

      {isOpen && (
        <>
          {/* ★ Default version virtual row */}
          <DefaultVersionRow
            app={app}
            defaults={defaults}
            onDefaultScopeToggle={onDefaultScopeToggle}
          />
          {app.versions.map(v => (
            <VersionRow
              key={v.id}
              app={app}
              version={v}
              defaults={defaults}
              onVersionToggle={onVersionToggle}
              onLaunchPreview={onLaunchPreview}
              previewPending={previewPending}
            />
          ))}
        </>
      )}
    </Fragment>
  );
}

function DefaultVersionRow({
  app, defaults, onDefaultScopeToggle,
}: {
  app: AppNode;
  defaults: PoolToggleDefaults;
  onDefaultScopeToggle: (app: AppNode, name: ToggleName) => void;
}) {
  const override = app.defaultScopeOverride;
  // Display states are rolled up from app-level defaults then default-scope override on top.
  const appPrecacheBase = appEffective(defaults.precache, app.appOverride?.precache);
  const appPrelaunchBase = appEffective(defaults.prelaunch, app.appOverride?.prelaunch);
  const appMultiplexBase = appEffective(defaults.multiplex, app.appOverride?.multiplex);
  const pre = displayFromOverride(appPrecacheBase, override?.precache);
  const pl = displayFromOverride(appPrelaunchBase, override?.prelaunch);
  const mx = displayFromOverride(appMultiplexBase, override?.multiplex);
  const overridden = hasDefaultScopeOverride(app);
  const rowClass = `${styles.row} ${styles.versionRow} ${styles.defaultVersionRow} ${overridden ? styles.rowOverridden : ''}`;

  return (
    <div className={rowClass}>
      <div className={styles.name}>
        <span className={styles.chevronSpacer} />
        <span className={styles.star}>★</span>
        <span className={styles.defaultVersionLabel}>Default version</span>
        <span className={styles.defaultVersionTarget}>
          (currently: {app.defaultVersionId ?? 'none set'})
        </span>
      </div>
      <div className={styles.cells}>
        <CellSpacer />
        <CellToggle on={pre.on} inherited={pre.inherited} onClick={() => onDefaultScopeToggle(app, 'precache')} />
        <CellToggle
          on={pl.on}
          inherited={pl.inherited}
          disabled={!pre.on}
          disabledReason={!pre.on ? 'Pre-launch requires Precache' : undefined}
          onClick={() => onDefaultScopeToggle(app, 'prelaunch')}
        />
        <CellToggle on={mx.on} inherited={mx.inherited} onClick={() => onDefaultScopeToggle(app, 'multiplex')} />
      </div>
      <CellSpacer />
    </div>
  );
}

function VersionRow({
  app, version, defaults: _defaults, onVersionToggle,
  onLaunchPreview, previewPending,
}: {
  app: AppNode;
  version: VersionNode;
  defaults: PoolToggleDefaults;
  onVersionToggle: (app: AppNode, v: VersionNode, name: ToggleName) => void;
  onLaunchPreview: (appId: string, versionId?: string) => void;
  previewPending: string | null;
}) {
  const isDefault = app.defaultVersionId === version.id;
  // Use the resolved values from the server — they already reflect all four layers.
  const r = version.resolved;
  // Display inheritance based on whether an explicit override row exists for this version.
  const override = version.versionOverride;
  const pre = { on: r.precache, inherited: !override || override.precache === null };
  const pl  = { on: r.prelaunch, inherited: !override || override.prelaunch === null };
  const mx  = { on: r.multiplex, inherited: !override || override.multiplex === null };
  const overridden = hasVersionOverride(version);
  const rowClass = `${styles.row} ${styles.versionRow} ${overridden ? styles.rowOverridden : ''}`;
  const previewKey = `${app.id}:${version.id}`;

  return (
    <div className={rowClass}>
      <div className={styles.name}>
        <span className={styles.chevronSpacer} />
        <span className={styles.chevronSpacer} />
        <span className={styles.versionId}>{version.id}</span>
        {isDefault && <span className={styles.currentDefaultBadge}>⭐ current default</span>}
        {version.isLocal && <span className={styles.localBadge}>local</span>}
        {!!version.gameEngine && <span className={styles.versionMeta}>{version.gameEngine}/{version.operatingSystem}</span>}
      </div>
      <div className={styles.cells}>
        <CellToggle on={r.provision} inherited={true} title="Version-level provision excludes not yet supported in UI" />
        <CellToggle on={pre.on} inherited={pre.inherited} onClick={() => onVersionToggle(app, version, 'precache')} />
        <CellToggle
          on={pl.on}
          inherited={pl.inherited}
          disabled={!pre.on}
          disabledReason={!pre.on ? 'Pre-launch requires Precache' : undefined}
          onClick={() => onVersionToggle(app, version, 'prelaunch')}
        />
        <CellToggle on={mx.on} inherited={mx.inherited} onClick={() => onVersionToggle(app, version, 'multiplex')} />
      </div>
      <PreviewCell
        targetKey={previewKey}
        pending={previewPending === previewKey}
        onClick={() => onLaunchPreview(app.id, version.id)}
        title={`Launch a preview session using ${version.id}`}
      />
    </div>
  );
}

/**
 * Right-most column on each row. Kicks off the admin preview-share flow for
 * this (app, version?) target, then navigates to `/preview/$shareId`.
 */
function PreviewCell({
  pending, onClick, disabled, title,
}: {
  targetKey: string;
  pending: boolean;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
}) {
  const cls = `${styles.previewButton} ${pending ? styles.previewButtonPending : ''}`;
  return (
    <div className={styles.previewCell}>
      <button
        className={cls}
        onClick={(e) => { e.stopPropagation(); onClick(); }}
        disabled={disabled || pending}
        title={title}
      >
        {pending ? '…' : '▶'}
      </button>
    </div>
  );
}

/**
 * Bulk provision toggle for org/project rows. Shows all/none/mixed state.
 * "all" = solid ON, "none" = outline OFF, "mixed" = hatched pattern.
 */
function BulkProvisionCell({
  state, onClick, disabled, title,
}: {
  state: 'all' | 'none' | 'mixed';
  onClick: (e: React.MouseEvent) => void;
  disabled?: boolean;
  title?: string;
}) {
  const cls = [
    styles.cell,
    state === 'all' ? styles.cellOn : state === 'none' ? styles.cellOff : styles.cellMixed,
    disabled ? styles.cellDisabled : '',
  ].filter(Boolean).join(' ');
  return (
    <button className={cls} onClick={onClick} disabled={disabled} title={title}>
      <span className={styles.cellDot}>{state === 'mixed' ? '◐' : '●'}</span>
      <span className={styles.cellState}>{state === 'all' ? 'all' : state === 'none' ? 'none' : 'mix'}</span>
    </button>
  );
}

function CellToggle({
  on, inherited, onClick, disabled, disabledReason, title,
}: {
  on: boolean;
  inherited: boolean;
  onClick?: () => void;
  disabled?: boolean;
  disabledReason?: string;
  title?: string;
}) {
  const cls = [
    styles.cell,
    on ? styles.cellOn : styles.cellOff,
    inherited ? styles.cellInherited : styles.cellExplicit,
    disabled ? styles.cellDisabled : '',
  ].filter(Boolean).join(' ');
  return (
    <button className={cls} onClick={onClick} disabled={disabled || !onClick} title={title ?? disabledReason}>
      <span className={styles.cellDot}>{inherited ? '·' : '●'}</span>
      <span className={styles.cellState}>{on ? 'on' : 'off'}</span>
    </button>
  );
}

function CellSpacer() {
  return <span className={styles.cellSpacer}>—</span>;
}
